// features/profile/edit/fields.tsx — form controls for the Edit Profile page (1.27.70).
// Same metrics and fills as ui/TextField so the page reads as part of the app;
// adds what the concept needs on top: a counter, a hint line, a leading icon and a
// clear button. Every control is labelled, and hint/error/counter are wired to
// it through aria-describedby.
import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import styled from 'styled-components';
import { Icon } from '@/icons';
import type { IconName } from '@/icons/names';
import { TextInput } from './fieldStyles';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Required = styled.span`
  margin-left: 3px;
  color: ${({ theme }) => theme.color.danger};
`;

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  min-height: 16px;
`;

const Hint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
  overflow-wrap: anywhere;
`;

const Err = styled(Hint)`
  color: ${({ theme }) => theme.color.danger};
`;

const Count = styled.span<{ $over: boolean }>`
  margin-left: auto;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme, $over }) => ($over ? theme.color.danger : theme.color.textMuted)};
`;

export interface FormFieldProps {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string;
  count?: { value: number; max: number };
  /** Rendered next to the label (a small control such as a visibility picker). */
  labelAside?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function FormField({
  label,
  required,
  hint,
  error,
  count,
  labelAside,
  children,
}: FormFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const countId = `${id}-count`;
  const describedBy =
    [error || hint ? hintId : null, count ? countId : null].filter(Boolean).join(' ') || undefined;
  return (
    <Wrap>
      <LabelRow>
        <Label htmlFor={id}>
          {label}
          {required && <Required aria-hidden="true">*</Required>}
        </Label>
        {labelAside}
      </LabelRow>
      {children({ id, describedBy, invalid: !!error })}
      {(hint || error || count) && (
        <Foot>
          {error ? (
            <Err id={hintId} role="alert">
              {error}
            </Err>
          ) : (
            hint && <Hint id={hintId}>{hint}</Hint>
          )}
          {count && (
            <Count id={countId} $over={count.value > count.max} aria-live="polite">
              {count.value}/{count.max}
            </Count>
          )}
        </Foot>
      )}
    </Wrap>
  );
}

const IconBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  > svg {
    position: absolute;
    left: 12px;
    color: ${({ theme }) => theme.color.textMuted};
    pointer-events: none;
  }
  > input {
    padding-left: 38px;
    padding-right: 38px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 6px;
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
  }
`;

export interface IconInputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon: IconName;
  invalid?: boolean;
  /** Shows a clear button while there is text; its accessible name. */
  clearLabel?: string;
  onClear?: () => void;
}

export function IconInput({ icon, invalid, clearLabel, onClear, ...input }: IconInputProps) {
  return (
    <IconBox>
      <Icon name={icon} size={16} />
      <TextInput $invalid={invalid} aria-invalid={invalid || undefined} {...input} />
      {onClear && input.value ? (
        <ClearButton type="button" aria-label={clearLabel} onClick={onClear}>
          <Icon name="close" size={14} />
        </ClearButton>
      ) : null}
    </IconBox>
  );
}
